
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { useBusinessSubscriptionStatus } from '@/hooks/useBusinessSubscriptionStatus';

interface BusinessSubscriptionBadgeProps {
  businessId: string;
  className?: string;
}

const BusinessSubscriptionBadge = ({ businessId, className = "" }: BusinessSubscriptionBadgeProps) => {
  const { data: subscriptionInfo, isLoading } = useBusinessSubscriptionStatus(businessId);

  if (isLoading) {
    return (
      <Badge variant="outline" className={`${className} animate-pulse`}>
        <Clock className="h-3 w-3 mr-1" />
        Loading...
      </Badge>
    );
  }

  if (!subscriptionInfo?.hasActiveSubscription) {
    return (
      <Badge variant="outline" className={`${className} text-muted-foreground`}>
        No Subscription
      </Badge>
    );
  }

  const getSubscriptionBadge = () => {
    const { subscriptionStatus, planName, isInGracePeriod } = subscriptionInfo;
    
    if (isInGracePeriod) {
      return (
        <Badge variant="outline" className={`${className} bg-amber-50 text-amber-700 border-amber-200`}>
          <AlertTriangle className="h-3 w-3 mr-1" />
          Canceled - Active
        </Badge>
      );
    }
    
    if (subscriptionStatus === 'active') {
      return (
        <Badge variant="outline" className={`${className} bg-green-50 text-green-700 border-green-200`}>
          <CheckCircle className="h-3 w-3 mr-1" />
          Active {planName ? `(${planName})` : ''}
        </Badge>
      );
    }

    if (subscriptionStatus === 'past_due') {
      return (
        <Badge variant="outline" className={`${className} bg-amber-50 text-amber-700 border-amber-200`}>
          <AlertTriangle className="h-3 w-3 mr-1" />
          Past Due
        </Badge>
      );
    }

    if (subscriptionStatus === 'trialing') {
      return (
        <Badge variant="outline" className={`${className} bg-blue-50 text-blue-700 border-blue-200`}>
          <Clock className="h-3 w-3 mr-1" />
          Trial
        </Badge>
      );
    }

    // For other active statuses
    return (
      <Badge variant="outline" className={`${className} bg-green-50 text-green-700 border-green-200`}>
        <CheckCircle className="h-3 w-3 mr-1" />
        Subscribed
      </Badge>
    );
  };

  return getSubscriptionBadge();
};

export default BusinessSubscriptionBadge;
